<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUiStore } from '@/stores/ui'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import BaixaTituloFinanceiro from '@/components/Financeiro/movimentacoesBancarias/BaixaTituloFinanceiro.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { formatarDataBR, isVencido } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const uiStore = useUiStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idTituloFinanceiro', label: 'ID', width: 80, sortable: true, slotName: 'idTituloFinanceiro' },
  { field: 'nomeParceiro', label: 'Parceiro', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'documentoTexto', label: 'Documento / Obs', slotName: 'documento', sortable: true },
  { field: 'tipoTexto', label: 'Tipo', align: 'center', slotName: 'tipo', sortable: true },
  { field: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true, slotName: 'dataVencimento' },
  { field: 'moedaTexto', label: 'Moeda', align: 'center', slotName: 'moedaTexto', sortable: true },
  {
    field: 'valorTotal',
    label: 'Valor Total',
    align: 'right',
    class: 'fw-medium',
    sortable: true,
    formatter: (v) => formatarValor(v)
  },
  { field: 'saldoDevedor', label: 'Saldo Devedor', align: 'right', slotName: 'saldoDevedor', sortable: true },
  { field: 'statusTexto', label: 'Status', align: 'center', slotName: 'status', sortable: true },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'moedaTexto') {
    return [...data].sort((a, b) => {
      const textoA = getTextoMoeda(a.lstMoeda)
      const textoB = getTextoMoeda(b.lstMoeda)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({
  tipos: [],
  status: [],
  moedas: []
})

const filtros = reactive({
  buscaLivre: '',
  lstTipoTitulo: '',
  lstStatusTitulo: '',
  dataEmissaoInicio: '',
  dataEmissaoFim: '',
  dataVencimentoInicio: '',
  dataVencimentoFim: '',
  dataBaixaInicio: '',
  dataBaixaFim: '',
  idParceiro: null,
  nomeParceiro: '',
  lstNaturezaTitulo: ''
})

// Determina se o filtro de tipo está fixo pelo Menu (Contas a Pagar / Contas a Receber)
const tipoFixoPorMenu = computed(() => {
  if (uiStore.currentModuleTitle?.includes('Pagar')) return 'FIN_TPTITULO1'
  if (uiStore.currentModuleTitle?.includes('Receber')) return 'FIN_TPTITULO2'
  return ''
})

const labelNovoBotao = computed(() => {
  if (tipoFixoPorMenu.value === 'FIN_TPTITULO1') return 'Novo Título a Pagar'
  if (tipoFixoPorMenu.value === 'FIN_TPTITULO2') return 'Novo Título a Receber'
  return 'Novo Título'
})

const configBuscaParceiro = computed(() => {
  const baseFiltros = { idEmpresa: authStore.empresaAtiva }
  const tipoAFiltrar = tipoFixoPorMenu.value || filtros.lstTipoTitulo
  if (tipoAFiltrar === 'FIN_TPTITULO1') baseFiltros.isFornecedor = true
  if (tipoAFiltrar === 'FIN_TPTITULO2') baseFiltros.isCliente = true

  return {
    ...parceiroSearch,
    titulo: 'Buscar Parceiro de Negócios',
    filtrosFixos: baseFiltros
  }
})

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.tipos = []
    listas.status = []
    listas.moedas = []
  }
})

watch(tipoFixoPorMenu, () => {
  buscarDados(1)
})

const carregarListas = async () => {
  try {
    const [resTipos, resStatus, resMoedas] = await Promise.all([
      api.get('/ListasSistema/lookup/FIN_TPTITULO'),
      api.get('/ListasSistema/lookup/ST_TITULO_FIN'),
      api.get('/ListasSistema/lookup/MOE')
    ])
    listas.tipos = resTipos.data
    listas.status = resStatus.data
    listas.moedas = resMoedas.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)

    // Força o filtro baseado no módulo atual se não houver um filtro explícito na tela
    const tipoFiltro = tipoFixoPorMenu.value || filtros.lstTipoTitulo
    if (tipoFiltro) params.append('lstTipoTitulo', tipoFiltro)

    if (filtros.lstStatusTitulo) params.append('lstStatusTitulo', filtros.lstStatusTitulo)
    if (filtros.lstNaturezaTitulo) params.append('lstNaturezaTitulo', filtros.lstNaturezaTitulo)
    if (filtros.idParceiro) params.append('idParceiro', filtros.idParceiro)
    if (filtros.dataEmissaoInicio) params.append('dataEmissaoInicio', filtros.dataEmissaoInicio)
    if (filtros.dataEmissaoFim) params.append('dataEmissaoFim', filtros.dataEmissaoFim)
    if (filtros.dataVencimentoInicio) params.append('dataVencimentoInicio', filtros.dataVencimentoInicio)
    if (filtros.dataVencimentoFim) params.append('dataVencimentoFim', filtros.dataVencimentoFim)
    if (filtros.dataBaixaInicio) params.append('dataBaixaInicio', filtros.dataBaixaInicio)
    if (filtros.dataBaixaFim) params.append('dataBaixaFim', filtros.dataBaixaFim)

    const response = await api.get(`/TitulosFinanceiros?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os títulos financeiros.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.buscaLivre = ''
  filtros.lstTipoTitulo = ''
  filtros.lstStatusTitulo = ''
  filtros.lstNaturezaTitulo = ''
  filtros.idParceiro = null
  filtros.nomeParceiro = ''
  filtros.dataEmissaoInicio = ''
  filtros.dataEmissaoFim = ''
  filtros.dataVencimentoInicio = ''
  filtros.dataVencimentoFim = ''
  filtros.dataBaixaInicio = ''
  filtros.dataBaixaFim = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarRegistro = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/TitulosFinanceiros/${item.idTituloFinanceiro}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do título.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idTituloFinanceiro
  auditoriaSelecionada.nome = `Título ${item.idTituloFinanceiro} - ${item.nomeParceiro}`
  mostrarAuditoria.value = true
}

const getTextoMoeda = (idMoeda) => {
  if (!idMoeda) return '-'
  const idStr = String(idMoeda)
  const moeda = listas.moedas.find(m => String(m.idListaSistemaItem) === idStr)
  return moeda ? moeda.textoLista : idMoeda
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const obterCorStatus = (lstStatus) => {
  if (lstStatus?.includes('ABERTO')) return 'badge-ghost-warning'
  if (lstStatus?.includes('PAGO_PARCIAL')) return 'badge-ghost-primary'
  if (lstStatus?.includes('BAIXADO') || lstStatus?.includes('QUITADO')) return 'badge-ghost-success'
  if (lstStatus?.includes('CANCELADO')) return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

const obterNomeTipo = (item) => {
  const idBusca = item.lstTipoTitulo || item.nomeTipoTitulo
  const encontrado = listas.tipos.find(t => t.idListaSistemaItem === idBusca)
  if (encontrado) return encontrado.textoLista
  return item.nomeTipoTitulo || item.lstTipoTitulo || '-'
}

const obterNomeStatus = (item) => {
  const idBusca = item.lstStatusTitulo || item.nomeStatusTitulo
  const encontrado = listas.status.find(s => s.idListaSistemaItem === idBusca)
  if (encontrado) return encontrado.textoLista
  return item.nomeStatusTitulo || item.lstStatusTitulo || '-'
}

// --- BAIXA / PAGAMENTO ---
const isModalBaixaOpen = ref(false)
const itemParaBaixa = ref(null)

const abrirModalBaixa = (item) => {
  itemParaBaixa.value = item
  isModalBaixaOpen.value = true
}

const onBaixaSalva = () => {
  isModalBaixaOpen.value = false
  buscarDados(paginacao.value.CurrentPage)
}

watch(() => uiStore.currentModuleTitle, () => {
  limparFiltros()
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    :titulo="uiStore.currentModuleTitle || 'Títulos Financeiros'"
    subtitulo="Gestão Operacional de Tesouraria"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    :label-botao-adicionar="labelNovoBotao"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Livre (Doc / Obs)</label>
          <input type="text" class="form-control" v-model="filtros.buscaLivre" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Tipo de Título</label>
          <select class="form-select" :value="tipoFixoPorMenu || filtros.lstTipoTitulo" @change="e => filtros.lstTipoTitulo = e.target.value" :disabled="!!tipoFixoPorMenu">
            <option value="">Todos</option>
            <option v-for="t in listas.tipos" :key="t.idListaSistemaItem" :value="t.idListaSistemaItem">
              {{ t.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusTitulo" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoBuscaLista id="lstNaturezaTitulo" label="Natureza da Operação" tabela="TituloFinanceiro" v-model="filtros.lstNaturezaTitulo" />
        </div>

        <div class="col-md-5">
          <CampoBusca id="filtroParceiro" label="Parceiro (Credor / Devedor)" v-model="filtros.idParceiro" :configuracao-busca="configBuscaParceiro" @selecionado="val => filtros.nomeParceiro = val.nome" />
        </div>

        <div class="col-md-3">
          <CampoDataIntervalo id="filtroVencimento" label="Período de Vencimento" v-model:modelValueInicio="filtros.dataVencimentoInicio" v-model:modelValueFim="filtros.dataVencimentoFim" />
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroEmissao" label="Período de Emissão" v-model:modelValueInicio="filtros.dataEmissaoInicio" v-model:modelValueFim="filtros.dataEmissaoFim" />
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroBaixa" label="Período de Baixa/Pagamento" v-model:modelValueInicio="filtros.dataBaixaInicio" v-model:modelValueFim="filtros.dataBaixaFim" />
        </div>

        <div class="col-md-3 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum título financeiro encontrado."
      row-key="idTituloFinanceiro"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegistro($event.row)"
    >
      <template #idTituloFinanceiro="{ row }">
        <span class="text-muted fw-medium">#{{ row.idTituloFinanceiro }}</span>
        <i v-if="row.isAdiantamento" class="bi bi-rocket-takeoff-fill text-warning ms-1" title="É um Adiantamento (ADT)"></i>
      </template>

      <template #documento="{ row }">
        <span v-if="row.numeroFaturaOriginal" class="fw-bold d-block">{{ row.numeroFaturaOriginal }}</span>
        <span class="text-truncate d-inline-block text-body-secondary small" style="max-width: 200px;" :title="row.observacaoLivre">
          {{ row.observacaoLivre || '-' }}
        </span>
      </template>

      <template #tipo="{ row }">
        <span class="badge bg-body-tertiary text-body border border-secondary-subtle mb-1 d-block">
          {{ obterNomeTipo(row) }}
        </span>
        <span class="fw-medium text-body-secondary" style="font-size: 0.8rem;">
          {{ row.nomeNaturezaTitulo }}
        </span>
      </template>

      <template #dataVencimento="{ row }">
        <span :class="{ 'text-danger fw-bold': isVencido(row.dataVencimento) && row.saldoDevedor > 0 }">
          {{ formatarDataBR(row.dataVencimento) }}
        </span>
      </template>

      <template #moedaTexto="{ row }">
        <span class="text-body-secondary">
          {{ getTextoMoeda(row.lstMoeda) }}
        </span>
      </template>

      <template #saldoDevedor="{ row }">
        <span :class="row.saldoDevedor > 0 ? 'text-primary fw-bold' : 'text-success fw-bold'">
          {{ formatarValor(row.saldoDevedor) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusTitulo)">
          {{ obterNomeStatus(row) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button v-if="row.saldoDevedor > 0 && temPermissaoCompleta" class="btn-icon text-success" @click="abrirModalBaixa(row)" title="Baixar / Pagar">
            <i class="bi bi-currency-dollar"></i>
          </button>
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="TituloFinanceiro"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />

  <BaixaTituloFinanceiro
    :is-open="isModalBaixaOpen"
    :titulo-financeiro="itemParaBaixa"
    @close="isModalBaixaOpen = false"
    @salvo="onBaixaSalva"
    @show-notification="(p) => emit('show-notification', p)"
  />
</template>
